import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Copy, Lock, Pencil, Trash2, CalendarPlus, CheckCircle2 } from "lucide-react";
import { useBuilderLang } from "@/lib/builderStrings";
import { calcApi, getActiveCalc, setActiveCalc } from "@/lib/customCalc";
import { getErrorMessage } from "@/lib/api";
import Loader from "@/components/Loader";

function CalcCard({ c, b, active, onUse, onDelete }) {
  return (
    <div className={`rounded-2xl bg-white border-2 p-4 transition-colors ${active ? "border-thali-green" : "border-thali-turmeric/30"}`} data-testid={`calc-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-heading font-extrabold truncate">{c.name}</div>
          <div className="text-xs text-thali-ink/60">{c.state} · {c.classes} classes · {c.items} items{c.version ? ` · ${b("version")}${c.version}` : ""}</div>
        </div>
        {c.locked ? (
          <span className="shrink-0 text-[11px] font-bold bg-thali-ink text-white rounded-full px-2 py-1 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> {b("locked")}</span>
        ) : (
          <span className={`shrink-0 text-[11px] font-bold rounded-full px-2 py-1 ${c.status === "published" ? "bg-thali-green/15 text-thali-green" : "bg-thali-turmeric/30 text-thali-ink"}`} data-testid={`calc-status-${c.id}`}>
            {c.status === "published" ? b("published") : b("draft")}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {active ? (
          <span className="text-xs font-bold text-thali-green inline-flex items-center gap-1 min-h-[40px]"><CheckCircle2 className="w-4 h-4" /> {b("active")}</span>
        ) : c.status === "published" && (
          <button onClick={onUse} className="btn-3d-green text-sm px-3" data-testid={`calc-use-${c.id}`}><CalendarPlus className="w-4 h-4" /> {b("use_in_entry")}</button>
        )}
        {c.locked ? (
          <Link to="/app/builder?from=odisha" className="btn-ghost text-sm px-3" data-testid="calc-copy-odisha"><Copy className="w-4 h-4" /> {b("copy_odisha")}</Link>
        ) : (
          <>
            <Link to={`/app/builder/${c.id}`} className="btn-ghost text-sm px-3" data-testid={`calc-edit-${c.id}`}><Pencil className="w-4 h-4" /> {b("edit")}</Link>
            <button onClick={onDelete} className="btn-ghost text-sm px-3 text-red-600" data-testid={`calc-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
          </>
        )}
      </div>
    </div>
  );
}

export default function MyCalculators() {
  const { b } = useBuilderLang();
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [active, setActive] = useState(getActiveCalc());
  const load = () => calcApi.list().then(setList).catch((e) => { toast.error(getErrorMessage(e)); setList([]); });
  useEffect(() => { load(); }, []);
  if (!list) return <Loader />;
  const use = (id) => { setActiveCalc(id); setActive(id); nav("/app/entry"); };
  const del = async (id) => {
    if (!window.confirm(b("delete_confirm"))) return;
    try { await calcApi.remove(id); if (active === id) { setActiveCalc("odisha"); setActive("odisha"); } load(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  return (
    <div className="space-y-5" data-testid="my-calculators">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold">{b("my_calcs")}</h1>
        <p className="text-sm text-thali-ink/60">{b("free_note")}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Link to="/app/builder" className="btn-3d-saffron" data-testid="calc-new"><Plus className="w-5 h-5" /> {b("new_calc")}</Link>
        <Link to="/app/builder?from=odisha" className="btn-ghost border-2 border-thali-green/40 text-thali-green" data-testid="calc-new-from-odisha"><Copy className="w-4 h-4" /> {b("copy_odisha")}</Link>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {list.map((c) => (
          <CalcCard key={c.id} c={c} b={b} active={active === c.id} onUse={() => use(c.id)} onDelete={() => del(c.id)} />
        ))}
      </div>
      {list.length === 1 && <p className="text-sm text-thali-ink/50" data-testid="calc-empty">{b("empty")}</p>}
    </div>
  );
}
